import React, { useRef } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { useTheme } from '../../contexts/ThemeContext';
import { MOBILE_UI } from '../../constants/mobileUi';

/**
 * Standard mobile dialog: slides up from the bottom with a grab handle, title row, and
 * safe-area padded body. Every mobile dialog uses this instead of a centered modal.
 *
 * @param {React.ReactNode} headerBelow - Optional controls pinned under the title row (e.g. tabs).
 */
const BottomSheet = ({
  show,
  onHide,
  title = null,
  titleExtra = null,
  children,
  footer = null,
  headerBelow = null,
  testId,
  maxHeight = 'min(72vh, 720px)',
  closeLabel = 'Close',
  bodyPadding = '12px 14px',
}) => {
  const { colors } = useTheme();
  const dragRef = useRef(null);

  const onHandlePointerDown = (event) => {
    if (event.button != null && event.button !== 0) return;
    const panel = event.currentTarget.closest('.offcanvas');
    if (!panel) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { y: event.clientY, id: event.pointerId, panel };
  };

  const onHandlePointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.id) return;
    const dy = Math.max(0, event.clientY - drag.y);
    drag.panel.style.transition = 'none';
    drag.panel.style.transform = dy ? `translateY(${dy}px)` : '';
  };

  const onHandlePointerUp = (event) => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.id) return;
    dragRef.current = null;
    const dy = Math.max(0, event.clientY - drag.y);
    drag.panel.style.transition = '';
    drag.panel.style.transform = '';
    if (dy > 72) onHide?.();
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="bottom"
      backdrop
      scroll
      data-testid={testId}
      style={{
        // Auto height so short sheets hug their content instead of reserving a fixed slab.
        height: 'auto',
        maxHeight,
        overflow: 'hidden',
        maxWidth: 560,
        marginLeft: 'auto',
        marginRight: 'auto',
        backgroundColor: colors.bgPrimary,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
      }}
    >
      <div
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        style={{
          display: 'flex',
          justifyContent: 'center',
          paddingTop: 8,
          paddingBottom: 10,
          flexShrink: 0,
          touchAction: 'none',
          cursor: 'grab',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 36,
            height: 4,
            borderRadius: 999,
            backgroundColor: colors.border,
          }}
        />
      </div>

      {(title || headerBelow) ? (
        <div
          style={{
            borderBottom: `1px solid ${colors.borderLight}`,
            padding: '6px 14px 10px',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: headerBelow ? 10 : 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
              <div
                style={{
                  color: colors.text,
                  fontSize: MOBILE_UI.font.title,
                  fontWeight: 700,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </div>
              {titleExtra ? (
                <div style={{ minWidth: 0, overflowX: 'auto', display: 'flex', alignItems: 'center' }}>
                  {titleExtra}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={onHide}
              aria-label={closeLabel}
              style={{
                width: MOBILE_UI.touchMin,
                height: MOBILE_UI.touchMin,
                padding: 0,
                border: 'none',
                borderRadius: MOBILE_UI.radiusControl,
                background: 'transparent',
                color: colors.textMuted,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              <X size={20} />
            </button>
          </div>
          {headerBelow}
        </div>
      ) : null}

      <Offcanvas.Body
        style={{
          backgroundColor: colors.bgPrimary,
          padding: footer
            ? bodyPadding
            : `${bodyPadding} calc(16px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        {children}
      </Offcanvas.Body>

      {footer ? (
        <div
          style={{
            borderTop: `1px solid ${colors.borderLight}`,
            padding: '10px 14px calc(12px + env(safe-area-inset-bottom, 0px))',
            backgroundColor: colors.bgPrimary,
            flexShrink: 0,
          }}
        >
          {footer}
        </div>
      ) : null}
    </Offcanvas>
  );
};

export default BottomSheet;
